import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import BackButton from '../components/BackButton';
import { validateIsraeliId, validateCompanyNumber } from '../lib/israeli-id';
import type { ClientType } from '../types';

const TYPE_OPTIONS: { value: ClientType; labelKey: string }[] = [
  { value: 'private', labelKey: 'types.private' },
  { value: 'business', labelKey: 'types.business' },
  { value: 'government', labelKey: 'types.government_short' },
];

const SOURCE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'facebook', labelKey: 'sources.facebook' },
  { value: 'site', labelKey: 'sources.site' },
  { value: 'landing', labelKey: 'sources.landing' },
  { value: 'referral', labelKey: 'sources.referral' },
];

// Tag *values* are stored on the client row (DB string array) — keep them as
// Hebrew strings so existing rows still match. Display label comes from i18n.
const TAG_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'VIP', labelKey: 'tags.vip' },
  { value: 'הפניה', labelKey: 'tags.referral' },
  { value: 'לקוח חוזר', labelKey: 'tags.returning' },
  { value: 'תשלום מיידי', labelKey: 'tags.instant_payment' },
  { value: 'דחוף', labelKey: 'tags.urgent' },
];

const LEGAL_AREAS: { value: string; key: string }[] = [
  { value: 'דיני משפחה', key: 'family' },
  { value: 'נדל״ן', key: 'real_estate' },
  { value: 'דיני עבודה', key: 'labor' },
  { value: 'מסחרי', key: 'commercial' },
  { value: 'פלילי', key: 'criminal' },
  { value: 'ירושה', key: 'inheritance' },
  { value: 'נזיקין', key: 'tort' },
  { value: 'אחר', key: 'other' },
];

const FEE_TYPES: ('hourly' | 'fixed' | 'contingency')[] = ['hourly', 'fixed', 'contingency'];

function initialsOf(first: string, last: string) {
  const f = first.trim()[0] ?? '';
  const l = last.trim()[0] ?? '';
  return (f + l) || '?';
}

export function AddClient({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const { t } = useTranslation(['clients', 'cases', 'common']);
  const [type, setType] = useState<ClientType>('private');
  const [source, setSource] = useState<string>('referral');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  // First-case fields (optional). See `clients:form.section_first_case`.
  const [caseArea, setCaseArea] = useState('');
  const [caseFeeType, setCaseFeeType] = useState<'hourly' | 'fixed' | 'contingency'>('hourly');
  const [caseHourlyRate, setCaseHourlyRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewName = useMemo(() => {
    const combined = `${firstName} ${lastName}`.trim();
    return combined || t('form.preview_default');
  }, [firstName, lastName, t]);

  const previewType = useMemo(() => {
    const opt = TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? t(opt.labelKey) : '';
  }, [type, t]);

  // Live ID hint — shown only once the user has typed something. Server
  // is still authoritative on submit.
  const idHint = useMemo(() => {
    if (!idNumber) return null;
    return type === 'business' ? validateCompanyNumber(idNumber) : validateIsraeliId(idNumber);
  }, [idNumber, type]);

  function toggleTag(tag: string) {
    setTags((prev) => (prev.includes(tag) ? prev.filter((tt) => tt !== tag) : [...prev, tag]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const name = `${firstName} ${lastName}`.trim();
      const hourlyRate = caseHourlyRate ? Number(caseHourlyRate) : null;
      const wantsCase = caseArea.trim() !== '' || (hourlyRate != null && hourlyRate > 0);
      await apiFetch('/api/clients', {
        method: 'POST',
        body: JSON.stringify({
          type,
          name,
          idNumber: idNumber || null,
          email: email || null,
          phone: phone || null,
          address: address || null,
          birthDate: birthDate || null,
          source,
          tags,
          notes: notes || null,
          firstCase: wantsCase
            ? {
                area: caseArea || null,
                feeType: caseFeeType,
                hourlyRateIls: hourlyRate,
              }
            : undefined,
        }),
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const errorKey = `errors.${err.code}`;
        const translated = t(errorKey, { defaultValue: '' });
        setError(translated || t('common:errors.code', { code: err.code }));
      } else {
        setError(err instanceof Error ? err.message : t('common:errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="form-wrap">
      <BackButton onClick={onCancel} />
      <form className="form-card" onSubmit={handleSubmit}>
        <div className="form-card-header">
          <div className="av-preview">{initialsOf(firstName, lastName)}</div>
          <div>
            <div className="form-preview-name">{previewName}</div>
            <div className="form-preview-type">{previewType}</div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_type')}</div>
          <div className="type-row">
            {TYPE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={`type-btn ${type === opt.value ? 'active' : ''}`}
                onClick={() => setType(opt.value)}
              >
                {t(opt.labelKey)}
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_source')}</div>
          <div className="type-row">
            {SOURCE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={`type-btn ${source === opt.value ? 'active' : ''}`}
                onClick={() => setSource(opt.value)}
              >
                {t(opt.labelKey)}
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_personal')}</div>
          <div className="field-grid">
            <div className="field">
              <label>{t('form.first_name')} <span className="req">*</span></label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                minLength={1}
                placeholder={t('form.first_name_placeholder')}
              />
            </div>
            <div className="field">
              <label>{t('form.last_name')} <span className="req">*</span></label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                minLength={1}
                placeholder={t('form.last_name_placeholder')}
              />
            </div>
            <div className="field">
              <label>
                {type === 'business' ? t('form.id_number_business') : t('form.id_number_personal')}
              </label>
              <input
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                placeholder={t('form.id_placeholder')}
                inputMode="numeric"
              />
              {idHint && idNumber.length >= 4 && (
                <small className={idHint.valid ? 'slug-status ok' : 'slug-status err'}>
                  {idHint.valid
                    ? `✓ ${t('id_validation.valid')}`
                    : t(`id_validation.${idHint.reason}`, { defaultValue: '' })}
                </small>
              )}
            </div>
            <div className="field">
              <label>{t('form.birth_date')}</label>
              <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_contact')}</div>
          <div className="field-grid">
            <div className="field">
              <label>{t('form.phone')} <span className="req">*</span></label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder={t('form.phone_placeholder')}
              />
            </div>
            <div className="field">
              <label>{t('form.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('form.email_placeholder')}
              />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>{t('form.address')}</label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t('form.address_placeholder')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_first_case')}</div>
          <div className="field-grid">
            <div className="field">
              <label>{t('cases:form.field_area')}</label>
              <select value={caseArea} onChange={(e) => setCaseArea(e.target.value)}>
                <option value="">{t('common:actions.select')}</option>
                {LEGAL_AREAS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {t(`cases:areas.${a.key}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('cases:form.field_fee_type')}</label>
              <select
                value={caseFeeType}
                onChange={(e) =>
                  setCaseFeeType(e.target.value as 'hourly' | 'fixed' | 'contingency')
                }
              >
                {FEE_TYPES.map((f) => (
                  <option key={f} value={f}>
                    {t(`cases:fee_types.${f}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('cases:form.field_hourly_rate')}</label>
              <input
                type="number"
                value={caseHourlyRate}
                onChange={(e) => setCaseHourlyRate(e.target.value)}
                placeholder={t('cases:form.field_hourly_rate_placeholder')}
                min={0}
                step={50}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_tags')}</div>
          <div className="tag-row">
            {TAG_OPTIONS.map((tag) => (
              <div
                key={tag.value}
                className={`tag ${tags.includes(tag.value) ? 'active' : ''}`}
                onClick={() => toggleTag(tag.value)}
              >
                {t(tag.labelKey)}
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_notes')}</div>
          <div className="field">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('form.notes_placeholder')}
            />
          </div>
        </div>

        {error && (
          <div className="form-section">
            <div className="signin-error">{error}</div>
          </div>
        )}

        <div className="form-footer">
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? t('common:actions.loading_dots') : t('form.save')}
          </button>
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={busy}>
            {t('common:actions.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
